<template>
    <div>
        <h1>MDH Search</h1>
        <div class="card">
            <div class="card-header">Search Licenses by Last Name, Nickname, or License #</div>
            <div class="card-body">
                <b-row role="alert">
                    <b-col>
                        <div class="sr-only hidden" v-if="isLoading">Loading, Please Wait...</div>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <form-summary :validator="$v.screen" ref="errorContainer" id="errorContainer"/>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col cols="4" md="4" id="lastNameDiv">
                        <BaseTextInput v-model="search.lastName"
                                       :v="$v.search.lastName"
                                       label="Last Name"
                                       slug="lastName"
                                       setId="lastName"/>
                    </b-col>
                    <b-col cols="4" md="4" id="licenseNbrDiv">
                        <BaseTextInput v-model="search.licenseNbr"
                                       :v="$v.search.licenseNbr"
                                       label="License Number"
                                       slug="licenseNbr"
                                       setId="licenseNbr"/>
                    </b-col>
                    <b-col cols="4" md="4" id="othNameDiv">
                        <BaseTextInput v-model="search.otherName"
                                       :v="$v.search.otherName"
                                       label="Trade/Other Name"
                                       slug="otherName"
                                       setId="otherName"/>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col cols="4" md="4" id="ssnDiv">
                        <BaseMaskedTextInput v-model="search.ssn"
                                             :v="$v.search.ssn"
                                             slug="ssn"
                                             label="Social Security Number"
                                             setId="ssn"
                                             mask="###-##-####"
                        />
                    </b-col>
                    <b-col cols="4" md="4" id="dobDiv">
                        <BaseDateOfBirthInput v-model="search.dob"
                                              :v="$v.search.dob"
                                              label="Date of Birth"
                                              slug="dob"
                                              setId="dob"/>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <b-button type="button" variant="primary" @click="searchLicenses">Search</b-button>
                    </b-col>
                </b-row>
                <div class="pt-3" v-if="submitted">
                    <b-row>
                        <b-col>
                            <h2>Results</h2>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <b-table hover small show-empty class="sr-caption"
                                     aria-live="polite"
                                     responsive="true"
                                     :fields="fields"
                                     :items="licenses"
                                     :busy="isLoading"
                                     label-sort-asc=""
                                     label-sort-desc=""
                                     caption="MDH Search table.  To sort the data in this table by a specific column,
                                        go to the appropriate column header cell and use enter to sort the data ascending.
                                        Use enter again to sort descending.">
                                <template v-slot:no-data>
                                    No Licenses Found.
                                </template>
                                <div slot="table-busy" class="text-center text-danger my-2">
                                    <b-spinner label="Loading, Please Wait" aria-label="Loading, Please Wait"
                                               class="align-middle"
                                               style="margin-right:5px; margin-top:12px;"
                                    ></b-spinner>
                                    <strong>Loading, Please Wait...</strong>
                                </div>
                                <template slot="empty" slot-scope="scope">
                                    No Licenses Found.
                                </template>
                                <template v-slot:cell(actions)="row">
                                    <b-button size="sm" @click="viewHistory(row.item)"
                                              variant="primary"
                                              class="mr-1" >
                                        View History
                                    </b-button>
                                    <b-button v-if="isAdmin()" size="sm" @click="adminUpdate(row.item)"
                                              variant="warning"
                                              class="mr-1" >
                                        Update Name, SSN, DOB, or Email/Username
                                    </b-button>
                                </template>
                            </b-table>
                        </b-col>
                    </b-row>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
    import axios from "axios"
    import axiosRetry from 'axios-retry'

    axiosRetry(axios, { retries: 3,
        retryDelay: axiosRetry.exponentialDelay  })

    export default {
        data() {
            return {
                licenses: [],
                fields: [
                    {key: "number", label: "License No.", sortable: true},
                    {key: "lastName", label: "Last Name", sortable: true},
                    {key: "firstName", label: "First Name", sortable: true},
                    {key: "middleName", label: "Middle Name", sortable: true},
                    {key: "otherNames", label: "Other Name(s)", sortable: true},
                    {key: "tradeNames", label: "Trade Name(s)", sortable: true},
                    {key: "effectiveDate", label: "Effective Date", sortable: true},
                    {key: "expirationDate", label: "Expiration Date", sortable: true},
                    {key: 'actions', label: 'Actions'}
                ],
                search :{
                    lastName: '',
                    licenseNbr: '',
                    otherName: '',
                    ssn: '',
                    dob: '',
                },
                submitted: false,
                isLoading: false,
            }
        },
        methods: {
            searchLicenses() {
                this.isLoading = true
                this.submitted = true;
                axios.post(`${process.env.VUE_APP_API_BASE_URL}/license/search`, this.search)
                    .then(response => {
                        this.licenses = response.data
                        this.isLoading = false
                    })
            },
            viewHistory(item) {
                this.$router.push({ name: 'history', params: { id: item.id } })
            },
            adminUpdate(item) {
                this.$router.push({ name: 'updateAdmin', params: { id: item.id } })
            },
            isAdmin() {
                if (this.$keycloak) {
                    return (this.$keycloak.hasResourceRole('mdh-admin') || this.$keycloak.hasResourceRole('mnit-admin'))
                } else {
                    return false;
                }
            }
        },
        validations() {
            return {
                search: {
                    lastName: {
                        required: false
                    },
                    licenseNbr: {
                        required: false
                    },
                    otherName: {
                        required: false
                    },
                    ssn: {
                        required: false
                    },
                    dob: {
                        required: false
                    }
                }
            }
        },
        created() {
            document.title = "MDH Search - Body Art"
        },
    }
</script>